import React, { useCallback, useEffect, useImperativeHandle } from "react";
import { useWatch } from "react-hook-form";
import CardTemplate from "components/card/card-template/CardTemplate";
import { FirstNameField, LastNameField, GenericDropdownField } from "components/form-fields";
import InfoPolicyBeneficiaryModal from "components/modal/info-policy-beneficiary/InfoPolicyBeneficiaryModal";
import type { PolicyBeneficiaryFormReturn, PolicyBeneficiaryRef, DTOApplicationFormReturn } from "types/Card";
import { BeneficiaryRelationshipsEnum } from "state/RootSchema";
import RELATIONSHIP_SELECT_OPTIONS from "constants/relationshipSelectOptions";
import { useContactInformation } from "hooks/useContactInformation";

interface PolicyBeneficiaryProps {
    className?: string;
    form: PolicyBeneficiaryFormReturn;
}

type PolicyBeneficiaryWithRef = PolicyBeneficiaryProps & {
    ref?: React.Ref<PolicyBeneficiaryRef>;
};

const PolicyBeneficiary = ({ ref, ...props }: PolicyBeneficiaryWithRef) => {
    const { className = "", form } = props;
    // ========== Hooks ==========
    const { contactData } = useContactInformation();

    const typedForm = form as DTOApplicationFormReturn;
    const {
        register,
        formState: { errors },
        setValue,
        trigger,
        getValues,
    } = typedForm;

    // ========== State ==========
    const policyBeneficiaryDataRedux = contactData?.policyBeneficiary;

    const policyBeneficiary = useWatch({
        control: typedForm.control as any,
        name: "policyBeneficiary",
    });

    // ========== Effects ==========
    // Restore form data from Redux
    useEffect(() => {
        if (policyBeneficiaryDataRedux) {
            setValue("policyBeneficiary.firstName", policyBeneficiaryDataRedux?.firstName);
            setValue("policyBeneficiary.lastName", policyBeneficiaryDataRedux?.lastName);
            setValue("policyBeneficiary.relationship", policyBeneficiaryDataRedux?.relationship);
        }
    }, [policyBeneficiaryDataRedux, setValue]);

    // ========== Event Handlers ==========
    const handleRelationshipChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            setValue("policyBeneficiary.relationship", e.target.value);
            trigger("policyBeneficiary.relationship");
        },
        [setValue, trigger]
    );

    // ========== Ref Methods ==========
    useImperativeHandle(
        ref,
        () => ({
            validate: () => trigger("policyBeneficiary"),
            getValues: () => {
                const formData = getValues("policyBeneficiary");
                return {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    relationship: formData.relationship as BeneficiaryRelationshipsEnum,
                };
            },
        }),
        [trigger, getValues]
    );

    // ========== Render ==========
    return (
        <CardTemplate
            title={
                <div className="flex items-center gap-2 p-2">
                    <h2 className="text-xl font-semibold text-base-content text-center">Your Policy Beneficiary</h2>
                    <InfoPolicyBeneficiaryModal />
                </div>
            }
            className={className}
        >
            <form className="flex flex-col" data-testid="policy-beneficiary-form" onSubmit={(e) => e.preventDefault()}>
                <p className="text-sm font-medium leading-5 text-base-content pb-5">
                    You can add additional beneficiaries as soon as your policy is in force.
                </p>

                <div className="flex gap-2 pb-3">
                    <FirstNameField
                        register={register("policyBeneficiary.firstName")}
                        error={errors.policyBeneficiary?.firstName?.message}
                        id="policy-beneficiary-firstName"
                        className="blur-upscope"
                    />
                    <LastNameField
                        register={register("policyBeneficiary.lastName")}
                        error={errors.policyBeneficiary?.lastName?.message}
                        id="policy-beneficiary-lastName"
                        className="blur-upscope"
                    />
                </div>

                <GenericDropdownField
                    label="Relationship"
                    register={register("policyBeneficiary.relationship", {
                        onChange: handleRelationshipChange,
                    })}
                    options={RELATIONSHIP_SELECT_OPTIONS}
                    value={policyBeneficiary?.relationship}
                    error={errors.policyBeneficiary?.relationship?.message}
                    id="policy-beneficiary-relationship"
                    className="w-full"
                    inputClassName="blur-upscope"
                />
            </form>
        </CardTemplate>
    );
};

PolicyBeneficiary.displayName = "PolicyBeneficiary";

export default PolicyBeneficiary;
